<template>
  <div class="pixso-page deliver-page" :class="{ 'deliver-page--en': isEnglish }">
    <section class="deliver-page__hero" :style="{ backgroundImage: `url(${heroBg})` }">
      <div class="deliver-page__hero-inner">
        <h1 class="pixso-text-96 deliver-page__hero-title">{{ t('DeliverIndex.hero.title') }}</h1>
        <p class="pixso-text-24 deliver-page__hero-desc">
          {{ t('DeliverIndex.hero.description') }}
        </p>
      </div>
    </section>

    <section class="deliver-page__story">
      <div class="deliver-page__container deliver-page__story-row">
        <img
          class="deliver-page__story-media"
          :src="productionLineImage"
          :alt="t('DeliverIndex.stories.production.alt')"
        />
        <div class="deliver-page__story-copy deliver-page__story-copy--narrow">
          <h2 class="pixso-text-64 deliver-page__story-title">{{ t('DeliverIndex.stories.production.title') }}</h2>
          <p class="pixso-text-20 deliver-page__story-desc">
            {{ t('DeliverIndex.stories.production.description') }}
          </p>
        </div>
      </div>
    </section>

    <section class="deliver-page__story deliver-page__story--second">
      <div class="deliver-page__container deliver-page__story-row">
        <div class="deliver-page__story-copy deliver-page__story-copy--wide">
          <h2 class="pixso-text-64 deliver-page__story-title">{{ t('DeliverIndex.stories.management.title') }}</h2>
          <p class="pixso-text-20 deliver-page__story-desc">
            {{ t('DeliverIndex.stories.management.description') }}
          </p>
        </div>
        <img
          class="deliver-page__story-media"
          :src="managementBaseImage"
          :alt="t('DeliverIndex.stories.management.alt')"
        />
      </div>
    </section>

    <section class="deliver-page__cnas">
      <div class="deliver-page__container deliver-page__cnas-intro">
        <p class="pixso-eyebrow deliver-page__eyebrow">{{ t('DeliverIndex.cnas.eyebrow') }}</p>
        <h2 class="pixso-text-96 deliver-page__cnas-title">
          {{ t('DeliverIndex.cnas.title1') }}<br />
          {{ t('DeliverIndex.cnas.title2') }}
        </h2>
        <p class="pixso-text-24 deliver-page__cnas-desc">
          {{ t('DeliverIndex.cnas.description') }}
        </p>
      </div>

      <div class="deliver-page__container deliver-page__analysis">
        <h3 class="pixso-text-64 deliver-page__section-title">{{ t('DeliverIndex.analysis.title') }}</h3>
        <p class="pixso-text-24 deliver-page__section-subtitle">{{ t('DeliverIndex.analysis.subtitle') }}</p>
        <div class="deliver-page__card-grid deliver-page__analysis-grid" :aria-label="t('DeliverIndex.analysis.alt')">
          <article
            v-for="item in analysisItems"
            :key="item.key"
            class="deliver-page__image-card deliver-page__analysis-card"
          >
            <img :src="item.image" :alt="t(item.altKey)" loading="lazy" />
            <div class="deliver-page__image-card-copy">
              <h4>{{ t(item.titleKey) }}</h4>
              <p data-text-reveal="off">{{ t(item.descriptionKey) }}</p>
            </div>
          </article>
        </div>
      </div>

      <section class="deliver-page__equipment">
        <div class="deliver-page__container">
          <h3 class="pixso-text-64 deliver-page__section-title">{{ t('DeliverIndex.equipment.title') }}</h3>
        </div>

        <div class="deliver-page__full-bleed deliver-page__scroll-band">
          <div
            class="deliver-page__marquee deliver-page__marquee--equipment"
            :aria-label="t('DeliverIndex.equipment.marqueeLabel')"
            @pointerenter="handleEquipmentPointerEnter"
            @pointerleave="handleEquipmentPointerLeave"
          >
            <div ref="equipmentTrackRef" class="deliver-page__marquee-track deliver-page__marquee-track--equipment">
              <template v-for="loop in 2" :key="loop">
                <div class="deliver-page__marquee-group">
                  <article
                    v-for="item in equipmentCards"
                    :key="`${loop}-${item.key}`"
                    class="deliver-page__image-card deliver-page__equipment-card"
                    :aria-hidden="loop === 2"
                  >
                    <img :src="item.image" :alt="loop === 1 ? t(item.altKey) : ''" loading="lazy" />
                    <div class="deliver-page__image-card-copy deliver-page__image-card-copy--compact">
                      <h4>{{ t(item.titleKey) }}</h4>
                    </div>
                  </article>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>
    </section>

    <section class="deliver-page__certs">
      <div class="deliver-page__container deliver-page__certs-intro">
        <p class="pixso-eyebrow deliver-page__eyebrow">{{ t('DeliverIndex.certs.eyebrow') }}</p>
        <h2 class="pixso-text-96 deliver-page__certs-title">{{ t('DeliverIndex.certs.title') }}</h2>
        <p class="pixso-text-24 deliver-page__certs-desc">{{ t('DeliverIndex.certs.description') }}</p>
      </div>

      <div class="deliver-page__full-bleed deliver-page__full-bleed--certs deliver-page__scroll-band">
        <div
          class="deliver-page__marquee deliver-page__marquee--certs"
          :aria-label="t('DeliverIndex.certs.marqueeLabel')"
          @pointerenter="handleCertPointerEnter"
          @pointerleave="handleCertPointerLeave"
        >
          <div ref="certTrackRef" class="deliver-page__marquee-track deliver-page__marquee-track--certs">
            <template v-for="loop in 2" :key="loop">
              <div class="deliver-page__marquee-group deliver-page__marquee-group--certs">
                <article
                  v-for="item in certCards"
                  :key="`${loop}-${item.key}`"
                  class="deliver-page__cert-card"
                  :aria-hidden="loop === 2"
                >
                  <img :src="item.image" :alt="loop === 1 ? t(item.altKey) : ''" loading="lazy" />
                  <div class="deliver-page__cert-copy">
                    <h4>{{ t(item.titleKey) }}</h4>
                    <p data-text-reveal="off">{{ t(item.descriptionKey) }}</p>
                  </div>
                </article>
              </div>
            </template>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import heroBg from '@/assets/images/pixso-deliver/hero-calibration-production-area.jpg'
import productionLineImage from '@/assets/images/pixso-deliver/production-line-official.jpg'
import managementBaseImage from '@/assets/images/pixso-deliver/management-base-official.jpg'
import analysisEnvironmentImage from '@/assets/images/pixso-deliver/analysis-environment.jpg'
import analysisElectricalImage from '@/assets/images/pixso-deliver/analysis-electrical.jpg'
import analysisEmcImage from '@/assets/images/pixso-deliver/analysis-emc.jpg'
import equipmentBciImage from '@/assets/images/pixso-deliver/equipment-bci.jpg'
import equipmentDropTesterImage from '@/assets/images/pixso-deliver/equipment-drop-tester.jpg'
import equipmentSaltSprayImage from '@/assets/images/pixso-deliver/equipment-salt-spray.jpg'
import equipmentThermalShockImage from '@/assets/images/pixso-deliver/equipment-thermal-shock.jpg'
import equipmentTransientPulseImage from '@/assets/images/pixso-deliver/equipment-transient-pulse.jpg'
import equipmentVibrationImage from '@/assets/images/pixso-deliver/equipment-vibration.jpg'
import equipmentWaterSprayImage from '@/assets/images/pixso-deliver/equipment-water-spray.jpg'
import certAspiceL2Image from '@/assets/images/pixso-deliver/cert-aspice-l2.jpg'
import certCnasLabImage from '@/assets/images/pixso-deliver/cert-cnas-lab.jpg'
import certIatf16949Image from '@/assets/images/pixso-deliver/cert-iatf16949.jpg'
import certIatf16949SecondImage from '@/assets/images/pixso-deliver/cert-iatf16949-2.jpg'
import certIso9001Image from '@/assets/images/pixso-deliver/cert-iso9001.jpg'
import certIso14001Image from '@/assets/images/pixso-deliver/cert-iso14001.jpg'
import certIso45001Image from '@/assets/images/pixso-deliver/cert-iso45001.jpg'
import certIso26262Image from '@/assets/images/pixso-deliver/cert-iso26262.jpg'
import certCx005AecQ100Image from '@/assets/images/pixso-deliver/cert-cx005-aec-q100.jpg'

const { locale, t } = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const analysisItems = [
  {
    key: 'environmentReliability',
    image: analysisEnvironmentImage,
    titleKey: 'DeliverIndex.analysis.items.environmentReliability.title',
    descriptionKey: 'DeliverIndex.analysis.items.environmentReliability.description',
    altKey: 'DeliverIndex.analysis.items.environmentReliability.alt'
  },
  {
    key: 'electricalPerformance',
    image: analysisElectricalImage,
    titleKey: 'DeliverIndex.analysis.items.electricalPerformance.title',
    descriptionKey: 'DeliverIndex.analysis.items.electricalPerformance.description',
    altKey: 'DeliverIndex.analysis.items.electricalPerformance.alt'
  },
  {
    key: 'emc',
    image: analysisEmcImage,
    titleKey: 'DeliverIndex.analysis.items.emc.title',
    descriptionKey: 'DeliverIndex.analysis.items.emc.description',
    altKey: 'DeliverIndex.analysis.items.emc.alt'
  }
]

const equipmentCards = [
  {
    key: 'dropTester',
    image: equipmentDropTesterImage,
    titleKey: 'DeliverIndex.equipment.items.dropTester.title',
    altKey: 'DeliverIndex.equipment.items.dropTester.alt'
  },
  {
    key: 'saltSprayChamber',
    image: equipmentSaltSprayImage,
    titleKey: 'DeliverIndex.equipment.items.saltSprayChamber.title',
    altKey: 'DeliverIndex.equipment.items.saltSprayChamber.alt'
  },
  {
    key: 'bciTester',
    image: equipmentBciImage,
    titleKey: 'DeliverIndex.equipment.items.bciTester.title',
    altKey: 'DeliverIndex.equipment.items.bciTester.alt'
  },
  {
    key: 'vibrationTable',
    image: equipmentVibrationImage,
    titleKey: 'DeliverIndex.equipment.items.vibrationTable.title',
    altKey: 'DeliverIndex.equipment.items.vibrationTable.alt'
  },
  {
    key: 'thermalShockChamber',
    image: equipmentThermalShockImage,
    titleKey: 'DeliverIndex.equipment.items.thermalShockChamber.title',
    altKey: 'DeliverIndex.equipment.items.thermalShockChamber.alt'
  },
  {
    key: 'waterSprayChamber',
    image: equipmentWaterSprayImage,
    titleKey: 'DeliverIndex.equipment.items.waterSprayChamber.title',
    altKey: 'DeliverIndex.equipment.items.waterSprayChamber.alt'
  },
  {
    key: 'transientPulseTester',
    image: equipmentTransientPulseImage,
    titleKey: 'DeliverIndex.equipment.items.transientPulseTester.title',
    altKey: 'DeliverIndex.equipment.items.transientPulseTester.alt'
  }
]

const createMarqueeBrakeController = ({
  baseSpeed,
  hoverSpeed,
  brakeDuration,
  resumeDuration
}) => {
  const trackRef = ref(null)

  let animationFrameId = 0
  let resizeFrameId = 0
  let groupWidth = 0
  let offset = 0
  let currentSpeed = baseSpeed
  let targetSpeed = baseSpeed
  let lastTimestamp = 0
  let prefersReducedMotion = false
  let transitionStartTime = 0
  let transitionDuration = resumeDuration
  let transitionStartSpeed = baseSpeed

  const updateTrackTransform = () => {
    if (trackRef.value) {
      trackRef.value.style.transform = `translate3d(${-offset}px, 0, 0)`
    }
  }

  const measureGroupWidth = () => {
    if (!trackRef.value) {
      return
    }

    const firstGroup = trackRef.value.querySelector('.deliver-page__marquee-group')
    if (!firstGroup) {
      return
    }

    groupWidth = firstGroup.getBoundingClientRect().width
    offset = groupWidth > 0 ? offset % groupWidth : 0
    updateTrackTransform()
  }

  const stopLoop = () => {
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId)
      animationFrameId = 0
    }
  }

  const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3)

  const startSpeedTransition = (nextSpeed, duration, timestamp = performance.now()) => {
    transitionStartTime = timestamp
    transitionDuration = duration
    transitionStartSpeed = currentSpeed
    targetSpeed = nextSpeed
  }

  const animateLoop = (timestamp) => {
    if (!trackRef.value) {
      return
    }

    if (!lastTimestamp) {
      lastTimestamp = timestamp
    }

    const deltaSeconds = Math.min((timestamp - lastTimestamp) / 1000, 0.05)
    lastTimestamp = timestamp

    if (prefersReducedMotion || groupWidth <= 0) {
      animationFrameId = window.requestAnimationFrame(animateLoop)
      return
    }

    const transitionProgress = transitionDuration > 0
      ? Math.min((timestamp - transitionStartTime) / transitionDuration, 1)
      : 1
    const easedProgress = easeOutCubic(transitionProgress)
    currentSpeed = transitionStartSpeed + (targetSpeed - transitionStartSpeed) * easedProgress

    if (transitionProgress >= 1) {
      currentSpeed = targetSpeed
    }

    offset = (offset + currentSpeed * deltaSeconds) % groupWidth

    updateTrackTransform()
    animationFrameId = window.requestAnimationFrame(animateLoop)
  }

  const startLoop = () => {
    stopLoop()
    lastTimestamp = 0
    prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    measureGroupWidth()

    if (prefersReducedMotion) {
      offset = 0
      currentSpeed = 0
      targetSpeed = 0
      updateTrackTransform()
      return
    }

    currentSpeed = targetSpeed
    transitionStartSpeed = currentSpeed
    transitionStartTime = performance.now()
    transitionDuration = resumeDuration
    animationFrameId = window.requestAnimationFrame(animateLoop)
  }

  const handleResize = () => {
    if (resizeFrameId) {
      window.cancelAnimationFrame(resizeFrameId)
    }

    resizeFrameId = window.requestAnimationFrame(() => {
      measureGroupWidth()
      resizeFrameId = 0
    })
  }

  const handlePointerEnter = () => {
    startSpeedTransition(hoverSpeed, brakeDuration)
  }

  const handlePointerLeave = () => {
    startSpeedTransition(baseSpeed, resumeDuration)
  }

  onBeforeUnmount(() => {
    stopLoop()
    if (resizeFrameId) {
      window.cancelAnimationFrame(resizeFrameId)
    }
  })

  return {
    trackRef,
    startLoop,
    handleResize,
    handlePointerEnter,
    handlePointerLeave
  }
}

const deliverMarqueeBrakeConfig = {
  baseSpeed: 88,
  hoverSpeed: 0,
  brakeDuration: 1100,
  resumeDuration: 820
}

const equipmentMarquee = createMarqueeBrakeController({
  ...deliverMarqueeBrakeConfig
})

const certMarquee = createMarqueeBrakeController({
  ...deliverMarqueeBrakeConfig
})

const {
  trackRef: equipmentTrackRef,
  startLoop: startEquipmentLoop,
  handleResize: handleEquipmentResize,
  handlePointerEnter: handleEquipmentPointerEnter,
  handlePointerLeave: handleEquipmentPointerLeave
} = equipmentMarquee

const {
  trackRef: certTrackRef,
  startLoop: startCertLoop,
  handleResize: handleCertResize,
  handlePointerEnter: handleCertPointerEnter,
  handlePointerLeave: handleCertPointerLeave
} = certMarquee

onMounted(() => {
  nextTick(() => {
    startEquipmentLoop()
    startCertLoop()
  })

  window.addEventListener('resize', handleWindowResize)
})

const handleWindowResize = () => {
  handleEquipmentResize()
  handleCertResize()
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowResize)
})

const certCards = [
  {
    key: 'aspiceL2',
    image: certAspiceL2Image,
    titleKey: 'DeliverIndex.certs.items.aspiceL2.title',
    descriptionKey: 'DeliverIndex.certs.items.aspiceL2.description',
    altKey: 'DeliverIndex.certs.items.aspiceL2.alt'
  },
  {
    key: 'cnasLab',
    image: certCnasLabImage,
    titleKey: 'DeliverIndex.certs.items.cnasLab.title',
    descriptionKey: 'DeliverIndex.certs.items.cnasLab.description',
    altKey: 'DeliverIndex.certs.items.cnasLab.alt'
  },
  {
    key: 'iatf16949',
    image: certIatf16949Image,
    titleKey: 'DeliverIndex.certs.items.iatf16949.title',
    descriptionKey: 'DeliverIndex.certs.items.iatf16949.description',
    altKey: 'DeliverIndex.certs.items.iatf16949.alt'
  },
  {
    key: 'iatf16949Second',
    image: certIatf16949SecondImage,
    titleKey: 'DeliverIndex.certs.items.iatf16949Second.title',
    descriptionKey: 'DeliverIndex.certs.items.iatf16949Second.description',
    altKey: 'DeliverIndex.certs.items.iatf16949Second.alt'
  },
  {
    key: 'iso9001',
    image: certIso9001Image,
    titleKey: 'DeliverIndex.certs.items.iso9001.title',
    descriptionKey: 'DeliverIndex.certs.items.iso9001.description',
    altKey: 'DeliverIndex.certs.items.iso9001.alt'
  },
  {
    key: 'iso14001',
    image: certIso14001Image,
    titleKey: 'DeliverIndex.certs.items.iso14001.title',
    descriptionKey: 'DeliverIndex.certs.items.iso14001.description',
    altKey: 'DeliverIndex.certs.items.iso14001.alt'
  },
  {
    key: 'iso45001',
    image: certIso45001Image,
    titleKey: 'DeliverIndex.certs.items.iso45001.title',
    descriptionKey: 'DeliverIndex.certs.items.iso45001.description',
    altKey: 'DeliverIndex.certs.items.iso45001.alt'
  },
  {
    key: 'iso26262',
    image: certIso26262Image,
    titleKey: 'DeliverIndex.certs.items.iso26262.title',
    descriptionKey: 'DeliverIndex.certs.items.iso26262.description',
    altKey: 'DeliverIndex.certs.items.iso26262.alt'
  },
  {
    key: 'cx005AecQ100',
    image: certCx005AecQ100Image,
    titleKey: 'DeliverIndex.certs.items.cx005AecQ100.title',
    descriptionKey: 'DeliverIndex.certs.items.cx005AecQ100.description',
    altKey: 'DeliverIndex.certs.items.cx005AecQ100.alt'
  }
]

</script>

<style scoped>
.deliver-page {
  --deliver-content-max: 1200px;
  --deliver-story-max: 1152px;
  --deliver-hero-height: var(--pixso-hero-height);
  --deliver-story-media-width: 564px;
  --deliver-story-media-height: 420px;
  --deliver-story-copy-padding-y: 10px;
  --deliver-story-second-top: 180px;
  --deliver-cnas-top: var(--pixso-section-gap-xl);
  --deliver-certs-top: var(--pixso-section-gap-xl);
  --deliver-certs-bottom: 378px;
  --deliver-mobile-story-top: 56px;
  --deliver-mobile-story-second-top: 120px;
  --deliver-mobile-radius: 20px;
  --deliver-tablet-certs-top: 240px;
  --deliver-card-min: 320px;
  --deliver-analysis-grid-max: 1152px;
  --deliver-equipment-card-width: 368px;
  --deliver-cert-card-width: 368px;
  width: 100%;
  background: var(--pixso-color-bg);
  color: var(--pixso-color-text);
  overflow-x: clip;
}

.deliver-page,
.deliver-page * {
  box-sizing: border-box;
}

.deliver-page img {
  display: block;
}

.deliver-page__container {
  width: min(var(--deliver-content-max), calc(100% - var(--pixso-page-padding) * 2));
  margin: 0 auto;
}

.deliver-page__hero {
  position: relative;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  height: var(--deliver-hero-height);
  min-height: var(--pixso-hero-min);
  background-position: center top;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.deliver-page__hero::before {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  content: '';
  pointer-events: none;
}

.deliver-page__hero-inner {
  position: relative;
  z-index: 1;
  width: min(var(--deliver-content-max), calc(100% - var(--pixso-page-padding) * 2));
  padding: 0 var(--pixso-fs-96);
  text-align: center;
}

.deliver-page__hero-title {
  margin: 0;
  color: var(--pixso-color-text);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.deliver-page__hero-desc {
  margin: var(--pixso-gap-md) auto 0;
  max-width: 100%;
  color: var(--pixso-color-text);
  font-weight: 700;
  line-height: var(--pixso-lh-24);
}

.deliver-page__story {
  padding-top: var(--pixso-fs-96);
}

.deliver-page__story--second {
  padding-top: var(--deliver-story-second-top);
}

.deliver-page__story-row {
  display: flex;
  width: min(var(--deliver-story-max), calc(100% - var(--pixso-page-padding) * 2));
  margin: 0 auto;
  align-items: flex-start;
  gap: var(--pixso-gap-md);
}

.deliver-page__story-media {
  width: var(--deliver-story-media-width);
  height: var(--deliver-story-media-height);
  border-radius: var(--pixso-radius-lg);
  object-fit: cover;
  object-position: center;
  flex: 0 0 auto;
}

.deliver-page__story-copy {
  width: var(--deliver-story-media-width);
  height: var(--deliver-story-media-height);
  padding: var(--deliver-story-copy-padding-y) var(--pixso-gap-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--pixso-fs-16);
  align-items: flex-start;
  justify-content: center;
}

.deliver-page__story-copy--narrow {
  max-width: none;
}

.deliver-page__story-copy--wide {
  max-width: none;
}

.deliver-page__story-title {
  margin: 0;
  color: var(--pixso-color-text);
  font-weight: 700;
}

.deliver-page--en .deliver-page__story-copy {
  padding-right: var(--pixso-gap-md);
  padding-left: var(--pixso-gap-md);
}

.deliver-page--en .deliver-page__story-title {
  font-size: clamp(36px, 2.75vw, var(--pixso-fs-48));
  line-height: clamp(44px, 3.2vw, var(--pixso-lh-48));
  overflow-wrap: normal;
  word-break: keep-all;
  hyphens: manual;
  text-wrap: balance;
}

.deliver-page__story-desc {
  margin: 0;
  color: var(--pixso-color-muted);
  font-weight: 700;
  line-height: var(--pixso-lh-20);
}

.deliver-page__cnas {
  padding-top: var(--deliver-cnas-top);
}

.deliver-page__cnas-intro {
  text-align: center;
}

.deliver-page__eyebrow {
  margin: 0;
  color: var(--pixso-color-primary);
}

.deliver-page__cnas-title {
  margin: var(--pixso-gap-sm) auto 0;
  color: var(--pixso-color-text);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.deliver-page__cnas-desc {
  margin: var(--pixso-gap-sm) auto 0;
  max-width: var(--deliver-content-max);
  color: var(--pixso-color-muted);
  font-weight: 700;
  line-height: var(--pixso-lh-24);
}

.deliver-page__analysis {
  margin-top: var(--pixso-gap-lg);
  text-align: center;
}

.deliver-page__section-title {
  margin: 0;
  color: var(--pixso-color-text);
  font-weight: 700;
  line-height: var(--pixso-lh-64);
}

.deliver-page__section-subtitle {
  margin: var(--pixso-gap-sm) 0 0;
  color: var(--pixso-color-muted);
  font-weight: 700;
  line-height: var(--pixso-lh-24);
}

.deliver-page__equipment {
  margin-top: var(--pixso-gap-lg);
  text-align: center;
}

.deliver-page__full-bleed {
  width: 100vw;
  margin-top: var(--pixso-gap-lg);
  margin-left: calc(50% - 50vw);
  overflow: hidden;
}

.deliver-page__full-bleed--certs {
  margin-top: var(--pixso-gap-lg);
}

.deliver-page__certs {
  padding-top: var(--deliver-certs-top);
  padding-bottom: var(--deliver-certs-bottom);
}

.deliver-page__certs-intro {
  text-align: center;
}

.deliver-page__certs-title {
  margin: var(--pixso-gap-sm) auto 0;
  color: var(--pixso-color-text);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
}

.deliver-page__certs-desc {
  margin: var(--pixso-gap-sm) auto 0;
  color: var(--pixso-color-muted);
  font-weight: 700;
  line-height: var(--pixso-lh-24);
}

.deliver-page__card-grid {
  margin-top: var(--pixso-gap-lg);
  display: grid;
  gap: var(--pixso-gap-md);
}

.deliver-page__analysis-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: min(100%, var(--deliver-analysis-grid-max));
  margin: var(--pixso-gap-sm) auto 0;
}

.deliver-page__image-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--pixso-radius-md);
  background: var(--pixso-color-surface);
  isolation: isolate;
}

.deliver-page__analysis-card {
  aspect-ratio: 368 / 240;
}

.deliver-page__equipment-card {
  width: var(--deliver-equipment-card-width);
  aspect-ratio: 368 / 240;
  flex: 0 0 auto;
}

.deliver-page__image-card::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.04) 26%, rgba(0, 0, 0, 0.72) 100%);
  pointer-events: none;
}

.deliver-page__image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.deliver-page__image-card-copy {
  position: absolute;
  left: var(--pixso-gap-md);
  right: var(--pixso-gap-md);
  bottom: var(--pixso-gap-md);
  z-index: 2;
  text-align: left;
  color: var(--pixso-color-text);
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.64);
}

.deliver-page__image-card-copy h4,
.deliver-page__cert-copy h4 {
  margin: 0;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
}

.deliver-page__image-card-copy p,
.deliver-page__cert-copy p {
  margin: var(--pixso-gap-sm) 0 0;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  color: rgba(255, 255, 255, 0.86);
}

.deliver-page__image-card-copy--compact h4 {
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
}

.deliver-page__scroll-band {
  padding-inline: 0;
}

.deliver-page__equipment .deliver-page__scroll-band {
  margin-top: var(--pixso-gap-sm);
}

.deliver-page__marquee {
  width: 100%;
  overflow: hidden;
}

.deliver-page__marquee--equipment {
  position: relative;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
}

.deliver-page__marquee-track {
  display: flex;
  width: max-content;
  will-change: transform;
  animation: deliver-marquee 34s linear infinite;
}

.deliver-page__marquee-group {
  display: flex;
  flex: 0 0 auto;
  gap: var(--pixso-gap-md);
  padding-right: var(--pixso-gap-md);
}

.deliver-page__marquee-group--certs {
  align-items: stretch;
}

.deliver-page__marquee-track--equipment {
  animation: none;
  transform: translate3d(0, 0, 0);
}

.deliver-page__marquee-track--certs {
  animation: none;
  transform: translate3d(0, 0, 0);
}

.deliver-page__cert-card {
  width: var(--deliver-cert-card-width);
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: var(--pixso-radius-md);
  background: var(--pixso-color-surface);
}

.deliver-page__cert-card img {
  width: 100%;
  height: 340px;
  object-fit: cover;
  object-position: top center;
}

.deliver-page__cert-copy {
  min-height: 118px;
  padding: var(--pixso-gap-md);
  color: var(--pixso-color-text);
  text-align: left;
}

.deliver-page__cert-copy p {
  color: var(--pixso-color-muted);
  text-shadow: none;
}

@keyframes deliver-marquee {
  from {
    transform: translate3d(0, 0, 0);
  }

  to {
    transform: translate3d(-50%, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .deliver-page__marquee-track {
    animation-play-state: paused;
  }
}

@media (max-width: 1200px) {
  .deliver-page {
    --deliver-certs-bottom: 0px;
  }

  .deliver-page__hero {
    min-height: var(--pixso-hero-min);
    height: var(--pixso-hero-height);
  }

  .deliver-page__hero-inner {
    width: min(var(--deliver-content-max), calc(100% - var(--pixso-page-padding) * 2));
    padding: 0 var(--pixso-gap-lg);
  }

  .deliver-page__story {
    padding-top: var(--pixso-fs-72);
  }

  .deliver-page__story--second {
    padding-top: var(--pixso-fs-96);
  }

  .deliver-page__cnas {
    padding-top: var(--deliver-story-second-top);
  }

  .deliver-page__certs {
    padding-top: var(--deliver-tablet-certs-top);
  }

}

@media (max-width: 1180px) {
  .deliver-page__story-media,
  .deliver-page__story-copy {
    width: calc((100% - var(--pixso-gap-md)) / 2);
    height: auto;
  }

  .deliver-page__story-media {
    aspect-ratio: 564 / 420;
  }

  .deliver-page__story-copy {
    min-height: var(--pixso-section-gap-xl);
    padding: var(--deliver-story-copy-padding-y) var(--pixso-gap-md);
  }
}

@media (max-width: 768px) {
  .deliver-page {
    --deliver-equipment-card-width: 272px;
    --deliver-cert-card-width: 256px;
  }

  .deliver-page__container {
    width: min(100%, calc(100% - var(--pixso-page-padding) * 2));
  }

  .deliver-page__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
    align-items: center;
  }

  .deliver-page__hero-inner {
    width: calc(100% - var(--pixso-page-padding) * 2);
    padding: 0 var(--pixso-gap-sm);
  }

  .deliver-page__hero-title {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .deliver-page__hero-desc {
    margin-top: var(--pixso-gap-sm);
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .deliver-page__story {
    padding-top: var(--deliver-mobile-story-top);
  }

  .deliver-page__story--second {
    padding-top: var(--deliver-mobile-story-second-top);
  }

  .deliver-page__story-media {
    width: 100%;
    height: auto;
    border-radius: var(--deliver-mobile-radius);
  }

  .deliver-page__story-copy {
    gap: var(--pixso-gap-sm);
  }

  .deliver-page__story-row {
    flex-direction: column;
    align-items: center;
  }

  .deliver-page__story-copy,
  .deliver-page__story-copy--narrow,
  .deliver-page__story-copy--wide {
    max-width: 100%;
    width: 100%;
    height: auto;
    padding: 0;
  }

  .deliver-page__story-title {
    font-size: var(--pixso-fs-28);
    line-height: var(--pixso-lh-28);
  }

  .deliver-page__story-desc {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .deliver-page__cnas {
    padding-top: var(--deliver-mobile-story-second-top);
  }

  .deliver-page__cnas-title,
  .deliver-page__certs-title {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .deliver-page__equipment .deliver-page__section-title,
  .deliver-page__certs-title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .deliver-page__cnas-desc,
  .deliver-page__section-subtitle,
  .deliver-page__certs-desc {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .deliver-page__analysis,
  .deliver-page__equipment {
    margin-top: var(--pixso-fs-32);
  }

  .deliver-page__full-bleed {
    margin-top: var(--pixso-gap-md);
  }

  .deliver-page__certs {
    padding-top: var(--deliver-mobile-story-second-top);
  }

  .deliver-page__full-bleed--certs {
    margin-top: var(--pixso-gap-lg);
  }

  .deliver-page__analysis-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .deliver-page__analysis-card {
    aspect-ratio: 368 / 252;
  }

  .deliver-page__equipment-card {
    width: min(calc(100vw - var(--pixso-page-padding) * 2), var(--deliver-equipment-card-width));
  }

  .deliver-page__marquee--equipment {
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 4%, #000 96%, transparent 100%);
    mask-image: linear-gradient(90deg, transparent 0, #000 4%, #000 96%, transparent 100%);
  }

  .deliver-page__cert-card {
    width: min(calc(100vw - var(--pixso-page-padding) * 2), var(--deliver-cert-card-width));
  }

  .deliver-page__cert-card img {
    height: 220px;
  }

  .deliver-page__cert-copy {
    min-height: 96px;
    padding: var(--pixso-gap-sm);
  }

  .deliver-page__image-card-copy--compact {
    left: var(--pixso-gap-sm);
    right: var(--pixso-gap-sm);
    bottom: var(--pixso-gap-sm);
  }

  .deliver-page__image-card-copy--compact h4,
  .deliver-page__cert-copy h4 {
    font-size: var(--pixso-fs-18);
    line-height: var(--pixso-lh-18);
  }

  .deliver-page__cert-copy p {
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
  }

}
</style>
